import * as React from "react";
import {
  Grid,
  Button,
  Box,
  Typography,
  FormControl,
  TextField,
  Alert,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie"; //  For cookie management
import md5 from "blueimp-md5"; //  Lightweight MD5 hashing

const SignInForm = () => {
  const navigate = useNavigate();

  // ✅ Form State
  const [formData, setFormData] = React.useState({
    emailAddress: "", //  Note: API expects "emailAddress", not "email"
    password: "",
  });

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [emailError, setEmailError] = React.useState(""); //  State for email validation error

  //  Email Validation Regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  //  Handle Input Changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    //  Validate email on change
    if (name === "emailAddress") {
      if (!emailRegex.test(value)) {
        setEmailError("Please enter a valid email address");
      } else {
        setEmailError("");
      }
    }
  };

  //  Handle Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    //  Check email validity before submission
    if (!emailRegex.test(formData.emailAddress)) {
      setEmailError("Please enter a valid email address");
      setLoading(false);
      return;
    }

    try {
      //  Hash password to MD5
      const hashedPassword = md5(formData.password);

      //  Get API base URL from .env
      const API_BASE_URL = process.env.REACT_APP_API_URL;

      //  Send login request
      const response = await fetch(`${API_BASE_URL}/PatientPortal/patientLogin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          emailAddress: formData.emailAddress, //  email field
          password: hashedPassword,
          pin: 0, // as per your spec
        }),
      });


      const result = await response.json();
      localStorage.setItem("neuro_patientportal_auth_token", result.token);
      localStorage.setItem("neuro_patientportal_userName", result.userName);


      console.log("here")

      //  Redirect to dashboard or home
      navigate("/dashboard/forms");

    } catch (err) {
      setError(err.message || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      className="auth-main-wrapper sign-in-area"
      sx={{
        py: { xs: "60px", md: "80px", lg: "100px", xl: "135px" },
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          maxWidth: { sm: "500px", md: "1255px" },
          mx: "auto",
          px: "12px",
        }}
      >
        <Grid
          container
          alignItems="center"
          columnSpacing={{ xs: 1, sm: 2, md: 4, lg: 3 }}
          sx={{
            flexWrap: "nowrap",
            overflowX: "hidden",
          }}
        >
          {/* Left Side - Image */}
          <Grid
            size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 7 }}
            sx={{
              display: { xs: "none", md: "block" },
              overflow: "hidden",
            }}
          >
            <img
              src="/images/sign-in.jpg"
              alt="sign-in visual"
              width="100%"
              height="auto"
              style={{
                borderRadius: "24px",
                maxHeight: "80vh",
                objectFit: "cover",
              }}
            />
          </Grid>

          {/* Right Side - Form */}
          <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 5 }}>
            <Box
              className="form-content"
              sx={{
                paddingLeft: { xs: "0", lg: "10px" },
              }}
            >
              {/* Logo */}
              <Box className="logo" sx={{
                mb: "23px", display: "flex", justifyContent: "flex-end", alignItems: "center",
                gap: 1, pr: 36
              }}>
                <img
                  src="/images/newlogo1.PNG"
                  alt="logo"
                  style={{ height: 45, width: "auto", display: "block" }} // keep aspect ratio
                />
                <Typography
                  variant="h6"
                  className="text-black"
                  sx={{
                    fontSize: { xs: "22px", sm: "25px", lg: "28px" },
                    mb: "7px",
                    fontWeight: "600",
                    lineHeight: 1,
                    ml: -1,
                    position: "relative",
                    top: 5, // push the text down a little; tweak 2–6 as needed
                  }}
                >
                  NeuroEZ
                </Typography>
              </Box>

              {/* Title */}
              <Box className="title" sx={{ mb: "23px" }}>
                <Typography
                  variant="h1"
                  className="text-black"
                  sx={{
                    fontSize: { xs: "22px", sm: "25px", lg: "28px" },
                    mb: "7px",
                    fontWeight: "600",
                  }}
                >
                  Welcome back to NeuroEZ Patient Portal
                </Typography>
                <Typography sx={{ fontWeight: "500", fontSize: "16px" }}>
                  Enter your credentials to access your account
                </Typography>
              </Box>

              {/* Error Message */}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Form */}
              <Box component="form" onSubmit={handleSubmit}>
                {/* Email Address */}
                <Box mb="15px">
                  <FormControl fullWidth>
                    <Typography
                      component="label"
                      sx={{
                        fontWeight: "500",
                        fontSize: "14px",
                        mb: "10px",
                        display: "block",
                      }}
                      className="text-black"
                    >
                      Email Address
                    </Typography>
                    <TextField
                      label="Enter your Email Address"
                      variant="filled"
                      id="emailAddress"
                      name="emailAddress"
                      value={formData.emailAddress}
                      onChange={handleChange}
                      required
                      error={!!emailError}
                      helperText={emailError}
                      sx={{
                        "& .MuiInputBase-root": {
                          border: "1px solid #D5D9E2",
                          backgroundColor: "#fff",
                          borderRadius: "7px",
                        },
                        "& .MuiInputBase-root::before": { border: "none" },
                        "& .MuiInputBase-root:hover::before": { border: "none" },
                        "& .MuiInputBase-root.Mui-focused::before": {
                          border: "none !important",
                        },
                      }}
                    />
                  </FormControl>
                </Box>

                {/* Password */}
                <Box mb="15px">
                  <FormControl fullWidth>
                    <Typography
                      component="label"
                      sx={{
                        fontWeight: "500",
                        fontSize: "14px",
                        mb: "10px",
                        display: "block",
                      }}
                      className="text-black"
                    >
                      Password
                    </Typography>
                    <TextField
                      label="Enter your password"
                      variant="filled"
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      sx={{
                        "& .MuiInputBase-root": {
                          border: "1px solid #D5D9E2",
                          backgroundColor: "#fff",
                          borderRadius: "7px",
                        },
                        "& .MuiInputBase-root::before": { border: "none" },
                        "& .MuiInputBase-root:hover::before": { border: "none" },
                        "& .MuiInputBase-root.Mui-focused::before": {
                          border: "none !important",
                        },
                      }}
                    />
                  </FormControl>
                </Box>

                {/* Sign In Button */}
                <Box mb="20px">
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || !!emailError}
                    sx={{
                      textTransform: "capitalize",
                      borderRadius: "6px",
                      fontWeight: "500",
                      fontSize: { xs: "13px", sm: "16px" },
                      padding: { xs: "10px 20px", sm: "10px 24px" },
                      color: "#fff !important",
                      boxShadow: "none",
                      width: "100%",
                    }}
                  >
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default SignInForm;